'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'

interface DocArticle {
  id: string
  title: string
  category: string
  summary: string
  content: string | string[]
  tags: string[]
}

export default function DocsClient({ articles, isAdmin }: { articles: DocArticle[]; isAdmin?: boolean }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')

  const categories = ['All', 'Core Concepts', 'Developer Tools', 'Platform Economy', 'Community', 'Integrations']

  const filteredDocs = useMemo(() => {
    return articles.filter(doc => {
      const matchesCategory = selectedCategory === 'All' || doc.category === selectedCategory
      const query = searchQuery.toLowerCase()
      const contentStr = Array.isArray(doc.content) ? doc.content.join(' ') : doc.content
      const matchesSearch = 
        doc.title.toLowerCase().includes(query) ||
        doc.summary.toLowerCase().includes(query) ||
        contentStr.toLowerCase().includes(query) ||
        doc.tags.some(t => t.toLowerCase().includes(query))
      return matchesCategory && matchesSearch
    })
  }, [articles, searchQuery, selectedCategory])

  return (
    <div className="space-y-8">
      {/* Header & Search */}
      <div className="bg-gradient-to-br from-[#070b14] via-[#04060b] to-[#020408] border border-indigo-500/40 rounded-2xl p-8 sm:p-10 shadow-2xl space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-indigo-950 border border-indigo-500/30 text-indigo-400 font-mono text-xs font-bold uppercase tracking-widest">
              <span>📖</span> Documentation &amp; Knowledge Base
            </div>
            <h1 className="text-3xl font-black text-white tracking-tight">
              DevYield <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">System Manual</span>
            </h1>
          </div>

          <div className="flex items-center gap-4">
            {isAdmin && (
              <Link
                href="/docs/new"
                className="bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold px-4 py-2 rounded-xl transition-all shadow-lg shadow-indigo-600/30 uppercase tracking-wider font-mono text-xs"
              >
                + Add Documentation
              </Link>
            )}
            <div className="font-mono text-xs text-slate-400">
              Status: <span className="text-emerald-400 font-bold">ONLINE</span> &bull; v2.4
            </div>
          </div>
        </div>

        {/* Search Input */}
        <div className="relative">
          <span className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 font-mono">
            🔍
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documentation (e.g. 'cli', 'vault', 'bounties', 'fork')..."
            className="w-full bg-[#020408] border border-slate-800 rounded-xl pl-11 pr-4 py-3.5 text-xs sm:text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono transition-all shadow-inner"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-xs text-slate-500 hover:text-white font-mono"
            >
              [clear]
            </button>
          )}
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 pt-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer ${
                selectedCategory === cat
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                  : 'bg-slate-900/80 text-slate-400 hover:text-white border border-slate-800'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Results Grid */}
      <div className="space-y-6">
        <div className="flex items-center justify-between text-xs font-mono text-slate-400 px-1">
          <span>Showing {filteredDocs.length} documentation article{filteredDocs.length === 1 ? '' : 's'}</span>
          {searchQuery && <span>Filtered by query: &quot;{searchQuery}&quot;</span>}
        </div>

        {filteredDocs.length === 0 ? (
          <div className="bg-[#070b14] border border-slate-800 rounded-2xl p-16 text-center space-y-3 font-mono text-xs text-slate-500 shadow-2xl">
            <div className="text-3xl">🔍</div>
            <div className="text-sm font-bold text-slate-300">No matching documentation found.</div>
            <div>Try searching for another keyword or reset your category filter.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredDocs.map(doc => (
              <Link 
                key={doc.id}
                href={`/docs/${doc.id}`}
                className="block bg-[#070b14] border border-slate-800/80 hover:border-indigo-500/50 rounded-2xl p-6 sm:p-8 space-y-4 shadow-xl transition-all group"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800/80 pb-4">
                  <div className="space-y-1">
                    <span className="text-[10px] font-mono font-bold text-indigo-400 uppercase tracking-widest bg-indigo-950/80 px-2.5 py-1 rounded border border-indigo-500/30">
                      {doc.category}
                    </span>
                    <h2 className="text-lg font-bold text-white group-hover:text-indigo-300 transition-colors pt-2">
                      {doc.title} &rarr;
                    </h2>
                  </div>
                  <div className="flex flex-wrap gap-1.5 font-mono text-[10px]">
                    {doc.tags.map(tag => (
                      <span key={tag} className="px-2 py-0.5 bg-slate-900 text-slate-400 rounded border border-slate-800">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-slate-300 font-sans leading-relaxed line-clamp-2">
                  {doc.summary}
                </p>

                <div className="pt-2 flex items-center justify-between text-xs font-mono text-indigo-400 font-bold">
                  <span>Read full guide &rarr;</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}